/*
 * <video-hero-media> — steuert das Hintergrundvideo im Hero.
 *
 * Das <video> traegt `preload="none"` und kein `autoplay`: ohne Zutun laedt der
 * Browser also nichts und zeigt nur das Poster. Erst wenn der Hero im Viewport
 * ist, wird abgespielt; scrollt er heraus, wird pausiert (spart Datenvolumen und
 * CPU, besonders auf Mobilgeraeten).
 *
 * Barrierefreiheit: WCAG 2.2 SC 2.2.2 verlangt fuer automatisch startende
 * Bewegung, die laenger als 5 Sekunden laeuft, eine Bedienmoeglichkeit — daher
 * der Pause/Play-Button. Wer `prefers-reduced-motion: reduce` gesetzt hat,
 * bekommt gar kein Autoplay, kann das Video aber bewusst starten.
 */
class VideoHeroMedia extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.selectSource();

    this.toggle = this.querySelector('[data-video-hero-toggle]');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Vom Nutzer bewusst pausiert — dann nicht durch Scrollen wieder starten.
    this.pausedByUser = false;

    if (this.toggle) {
      this.toggle.addEventListener('click', () => {
        this.pausedByUser = !this.video.paused;
        if (this.video.paused) {
          this.play();
        } else {
          this.pause();
        }
      });
    }

    this.video.addEventListener('play', () => this.syncToggle());
    this.video.addEventListener('pause', () => this.syncToggle());
    this.syncToggle();

    if (this.reduceMotion.matches) {
      this.pausedByUser = true;
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.play();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this.pausedByUser) this.play();
          } else if (!this.video.paused) {
            this.video.pause();
          }
        });
      },
      { rootMargin: '100px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  /*
   * Auf schmalen Viewports die kleinere Rendition nehmen. Das geschieht vor dem
   * ersten play() — dank preload="none" wurde noch nichts geladen, ein Wechsel
   * kostet also nichts. Ohne data-src-mobile bleibt die Desktop-Quelle stehen.
   */
  selectSource() {
    const source = this.querySelector('source');
    if (!source) return;
    const mobileSrc = source.dataset.srcMobile;
    if (!mobileSrc) return;
    if (window.matchMedia('(max-width: 749px)').matches && source.src !== mobileSrc) {
      source.src = mobileSrc;
      this.video.load();
    }
  }

  play() {
    const playback = this.video.play();
    // Autoplay kann trotz `muted` scheitern (z. B. Energiesparmodus). Dann
    // bleibt das Poster stehen und der Button zeigt korrekt "Abspielen".
    if (playback && typeof playback.catch === 'function') {
      playback.catch(() => this.syncToggle());
    }
  }

  pause() {
    this.video.pause();
  }

  syncToggle() {
    if (!this.toggle) return;
    const playing = !this.video.paused;
    // aria-pressed steuert per CSS auch, welches der beiden Icons sichtbar ist.
    this.toggle.setAttribute('aria-pressed', playing ? 'false' : 'true');
    const label = playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
    if (label) this.toggle.setAttribute('aria-label', label);
  }
}

if (!customElements.get('video-hero-media')) {
  customElements.define('video-hero-media', VideoHeroMedia);
}
